import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';

interface ProblematicCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (reason: string) => void;
  loading?: boolean;
}

const ProblematicCompleteModal: React.FC<ProblematicCompleteModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  loading = false
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim()) {
      onComplete(reason.trim());
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleQuickTextClick = (text: string) => {
    setReason(text);
  };

  const quickTexts = [
    t('modals.problematicComplete.quickText1'),
    t('modals.problematicComplete.quickText2'),
    t('modals.problematicComplete.quickText3'),
    t('modals.problematicComplete.quickText4'),
    t('modals.problematicComplete.quickText5'),
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.problematicComplete.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="modal-form-group">
          <label htmlFor="reason">{t('modals.problematicComplete.reasonLabel')}</label>

          {/* Quick Texts */}
          <div style={{ marginBottom: '0.75rem' }}>
            <small style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontWeight: 500 }}>
              {t('modals.problematicComplete.quickTexts')}
            </small>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {quickTexts.map((text, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleQuickTextClick(text)}
                  disabled={loading}
                  style={{
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.85rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    color: '#495057',
                    opacity: loading ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                      e.currentTarget.style.borderColor = '#adb5bd';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#dee2e6';
                    }
                  }}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('modals.problematicComplete.reasonPlaceholder')}
            required
            disabled={loading}
          />
        </div>

        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading}
          >
            {t('modals.problematicComplete.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button danger"
            disabled={loading || !reason.trim()}
          >
            {loading ? t('modals.problematicComplete.completing') : t('modals.problematicComplete.completeOrder')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProblematicCompleteModal;
